<template>
  <base-modal ref="modal" @hiding="reset()">
    <base-card shadow>
      <h3 class="tw-text-xl tw-font-thin">Activate Plan</h3>
      <div class="tw-text-gray-600 tw-text-sm tw-mt-3">This attempts the initial charge again which is required to start the plan.</div>
      <div class="tw-text-gray-800 tw-font-normal tw-mt-5">Choose Payment Method</div>
      <base-card class="tw-flex tw-flex-nowrap tw-mt-2" :class="{ 'tw-bg-primary-50': paymentOption === 'cardOnFile' }">
        <base-radio class="tw-mr-2" value="cardOnFile" name="paymentOption" v-model="paymentOption"></base-radio>
        <div class="tw-flex tw-justify-between tw-w-full">
          <div>
            <div class="tw-text-gray-800 tw-text-sm tw-font-normal">Card on file</div>
            <div class="tw-text-gray-500 tw-text-sm">Uses {{ organization.customerLabelSingular }}'s current default card on file.</div>
          </div>
          <div class="tw-flex tw-text-right">
            <credit-card-icon class="tw-w-8 tw-h-8 tw-my-auto" :name="savedCreditCard.brand" />
            <div class="tw-ml-3 tw-text-sm" v-if="savedCreditCard.last4">
              <div>**** {{ savedCreditCard.last4 }}</div>
              <div class="tw-text-gray-500 tw-text-xs">Exp {{ expDate(savedCreditCard.expMonth, savedCreditCard.expYear) }}</div>
            </div>
            <div class="tw-ml-3 tw-text-gray-500" v-else>
              No Card
            </div>
          </div>
        </div>
      </base-card>
      <base-card class="tw-flex tw-flex-nowrap tw-mt-2" :class="{ 'tw-bg-primary-50': paymentOption === 'manual' }">
        <base-radio class="tw-mr-2" value="manual" name="paymentOption" v-model="paymentOption"></base-radio>
        <div class="tw-flex tw-flex-col tw-w-full">
          <div class="tw-text-gray-800 tw-text-sm tw-font-normal">New card (replaces existing)</div>
          <base-credit-card-input class="tw-mt-3" ref="creditCardInput" />
        </div>
      </base-card>
      <div class="sm:tw-flex sm:tw-flex-row-reverse tw-mt-4">
        <base-button @click="activatePlan" :loading="isLoading" class="tw-w-full sm:tw-w-auto sm:tw-ml-3">Activate</base-button>
        <base-button @click="hide" type="flat" class="tw-w-full sm:tw-w-auto tw-mt-2 sm:tw-mt-0">
          Cancel
        </base-button>
      </div>
      <inline-error class="tw-mt-3" id="activate-business-member-plan-modal-error" :title="errorTitle" :message="errorMessage" :error="error" />
    </base-card>
  </base-modal>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import InlineError from "@/components/InlineError";
import { padStart } from "lodash";
import CreditCardIcon from "@/components/CreditCardIcon.vue";
import { loadStripe } from "@stripe/stripe-js/pure";

export default {
  name: "BusinessMemberPlanActivation",
  components: { InlineError, CreditCardIcon },
  props: {
    userId: String,
    businessId: String,
  },
  data() {
    return {
      plan: {},
      isLoading: false,
      paymentOption: "",
      savedCreditCard: { brand: "", last4: null, expMonth: 1, expYear: 2023 },
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    ...mapGetters("terminal", ["terminal", "reader", "connectionStatus"]),
    ...mapGetters("organization", ["organization"]),
    planPriceFormatted() {
      return this.priceCents ? new Intl.NumberFormat(this.locale, { style: "currency", currency: this.currency }).format(this.priceCents / 100) : "";
    },
    futureStart() {
      if (this.startDate) {
        var currentDate = moment(new Date());
        var utcStartDate = moment.utc(this.startDate, "YYYY-MM-DD-HH-mm-ss").local();
        if (utcStartDate > currentDate) return true;
        else return false;
      }
      return false;
    },
    formattedStartDate() {
      return moment
        .utc(this.startDate, "YYYY-MM-DD-HH-mm-ss")
        .local()
        .format("MMM DD, YYYY");
    },
  },
  created() {
    this.getCardOnFile();

    if (this.reader === null || this.connectionStatus !== "Connected") this.readerMessage = "No Readers Connected";
  },
  methods: {
    hide() {
      this.$refs.modal.hide();
    },
    expDate(expMonth, expYear) {
      if (expMonth && expYear) return `${padStart(expMonth, 2, "0")} / ${expYear.toString().slice(-2)}`;
      else return "unknown";
    },
    async show(plan) {
      await this.reset();
      this.$refs.modal.show();
      this.paymentOption = "cardOnFile";
      this.plan = plan;
    },
    async reset() {
      this.stripe = null;
      this.userBusinessPlanId = null;
      this.planName = null;
      this.priceCents = null;
      this.currency = null;
      this.startDate = null;
      this.message = null;
      this.showNewPayment = false;
      this.readerMessage = null;
      this.cardScanning = false;
      this.invoiceId = null;
      this.isRetryAvailable = false;
      this.clientSecret = null;
      this.stripeConnectedAccountId = null;
      this.stripeSubscriptionId = null;
    },
    getCardOnFile() {
      const url = `/businesses/${this.businessId}/members/${this.userId}/platformcard`;
      this.$axios
        .get(url)
        .then((res) => {
          if (res.data.last4) {
            this.savedCreditCard = res.data;
            this.paymentOption = "cardOnFile";
          } else {
            this.message = "manual";
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async activatePlan() {
      this.isLoading = true;
      let tokenId = null;
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;

      if (this.paymentOption == "manual") {
        var stripeToken = await this.$refs.creditCardInput.createToken();
        tokenId = stripeToken.id;
      }

      this.stripe = await loadStripe(this.$env.stripe_publishable_key, { stripeAccount: this.stripeConnectedAccountId });

      await this.$axios
        .post(`/businesses/${this.businessId}/members/${this.userId}/plans/${this.plan.userBusinessPlanId}/activate`, { stripeToken: tokenId })
        .then((res) => {
          this.stripeSubscriptionId = "has subscription";
          this.clientSecret = res.data.clientSecret;
          this.stripeConnectedAccountId = res.data.stripeConnectedAccountId;
          this.handleResponse(res.data.subscriptionStatus, res.data.paymentStatus, res.data.clientSecret, res.data.invoiceId);
        })
        .catch((err) => {
          this.errorTitle = "Error activating plan";
          this.errorMessage = "Sorry, there was an issue activating this plan.";
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async handleResponse(subscriptionStatus, paymentStatus, clientSecret, invoiceId) {
      this.invoiceId = invoiceId;

      if (subscriptionStatus === "active" && (paymentStatus === "succeeded" || paymentStatus === "paid")) {
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Payment Succeeded", message: "Member plan has been activated" });
        this.EventBus.$emit("member_update");
        this.$emit("activated");
        this.hide();
      } else if (subscriptionStatus === "active" && paymentStatus === "no payment status available") {
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Plan Waiting", message: "Member plan is active and will start on the specified start date." });
        this.EventBus.$emit("member_update");
        this.$emit("activated");
        this.hide();
      } else if (subscriptionStatus === "incomplete" && paymentStatus === "requires_payment_method") {
        this.errorTitle = "Payment failed";
        this.errorMessage = "The payment failed. Please update the customer's payment method to continue.";
        this.error = null;
      } else if (subscriptionStatus === "incomplete" && paymentStatus === "no payment status available") {
        this.errorTitle = "Payment failed";
        this.errorMessage = "The payment failed. Please update the customer's payment method to continue.";
        this.error = null;
      } else if (subscriptionStatus === "incomplete" && paymentStatus === "requires_action") {
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Action Required", message: "Your bank requires additional information" });

        //Show Stripe SCA box
        this.stripe.confirmCardPayment(clientSecret).then(
          async function(result) {
            if (result.error) {
              this.message = result.error.message;
            } else {
              await this.retryActivatePlan();
            }
          }.bind(this),
        );
      }
    },
  },
};
</script>
